import {Card, useDialog} from 'platform/components';
import {Box} from 'platform/foundation';

import {useCallback} from 'react';

import {mergeAll} from 'ramda';

import {
  useGetDocumentDatagridActions,
  DOCUMENT_ACTION_COLUMNS,
} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {
  DmsDataGrid,
  Section,
  UploadDocumentsDialog,
  refreshDmsDatagrid,
  usePermissions,
} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {EagGridMaximization, QueryFilterObject} from 'features/eag-grid';

interface DocumentsProps extends RequiredTestIdProps {
  maximization?: EagGridMaximization;
  onDocumentAction: VoidFunction;
}

export function Documents(props: DocumentsProps) {
  const {articleId} = useWarehouseParams();

  const [articleEdit] = usePermissions({
    permissionKeys: ['articleEdit'],
  });

  const [isUploadDialogOpen, openUploadDialog, closeUploadDialog] = useDialog();

  const [rowActions] = useGetDocumentDatagridActions({
    contextId: articleId,
    contextTarget: 'warehouse-article',
    refreshData: () => refreshDmsDatagrid(DOCUMENTS_GRID_CODE),
  });

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {warehouseArticleId: articleId}]),
    [articleId]
  );

  return (
    <Section>
      <Card
        title={i18n.t('entity.warehouse.labels.documents')}
        actions={[
          {
            type: 'button',
            variant: 'ghostLink',
            leftIcon: 'action/backup',
            title: i18n.t('entity.document.actions.uploadDocument'),
            onClick: openUploadDialog,
            isDisabled: !articleEdit,
            'data-testid': suffixTestId('action.addDocument', props),
          },
        ]}
      >
        <Box height="100%">
          <DmsDataGrid
            maximization={props.maximization}
            autoHeight
            gridCode={DOCUMENTS_GRID_CODE}
            actionCallback={rowActions}
            predefinedColumns={DOCUMENT_ACTION_COLUMNS}
            queryModifier={queryModifier}
            data-testid={DOCUMENTS_GRID_CODE}
          />
        </Box>
      </Card>

      <UploadDocumentsDialog
        isOpen={isUploadDialogOpen}
        handleClose={closeUploadDialog}
        onDocumentCreated={() => {
          refreshDmsDatagrid(DOCUMENTS_GRID_CODE);
          props.onDocumentAction();
        }}
        contextTarget="warehouse-article"
        contextId={articleId}
        bulkContext={[{target: 'warehouse-article', targetId: articleId}]}
        data-testid={suffixTestId('upload-document', props)}
      />
    </Section>
  );
}

const DOCUMENTS_GRID_CODE = 'document-context-list-warehouse-article';
